import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cancelBooking, getBookings, simpleRefundBooking } from '@openapi/bookings/bookings';
import {
    ALL_PAGES_PAGE_SIZE,
    MASS_OPERATION_TYPE,
    REFUND_MODE,
    calculateRefundableAmount,
    calculateAmountForRow,
    getBookingDisplayName,
    getErrorMessage,
} from './bookingsMassActionHelpers';

function useBookingsMassAction({
    rows,
    total,
    filters,
    canUseMassAction,
    isRowMassOperable,
    notifyError,
    notifySuccess,
    t,
    refreshBookings,
}) {
    const [selectedRowsById, setSelectedRowsById] = useState({});
    const [selectingAllPages, setSelectingAllPages] = useState(false);

    const [massModalOpen, setMassModalOpen] = useState(false);
    const [massOperationType, setMassOperationType] = useState(
        MASS_OPERATION_TYPE.CANCEL_AND_REFUND,
    );
    const [refundMode, setRefundMode] = useState(REFUND_MODE.FULL);
    const [refundValue, setRefundValue] = useState('100');

    const [isExecutingMassAction, setIsExecutingMassAction] = useState(false);
    const [massExecutionProgress, setMassExecutionProgress] = useState({ completed: 0, total: 0 });
    const [massResults, setMassResults] = useState([]);
    const [excludedFromMassActionById, setExcludedFromMassActionById] = useState({});
    const [maxRefundableSummary, setMaxRefundableSummary] = useState({
        loading: false,
        error: '',
        totalAmount: 0,
        bookings: [],
    });
    const refundableAmountCacheRef = useRef(new Map());
    const selectAllCacheRef = useRef({ key: '', rowsById: {} });
    const nonFullRefundValue = Number(refundValue) || 0;
    const isInvalidCancelRefundAmount =
        (massOperationType === MASS_OPERATION_TYPE.CANCEL_AND_REFUND ||
            massOperationType === MASS_OPERATION_TYPE.REFUND_ONLY) &&
        refundMode !== REFUND_MODE.FULL &&
        nonFullRefundValue <= 0;

    const selectedRows = useMemo(() => Object.values(selectedRowsById), [selectedRowsById]);
    const selectedCount = selectedRows.length;
    const selectedIds = useMemo(() => new Set(selectedRows.map((row) => row.id)), [selectedRows]);
    const selectedRowsForMassAction = useMemo(
        () => selectedRows.filter((row) => !excludedFromMassActionById[row.id]),
        [excludedFromMassActionById, selectedRows],
    );
    const massSelectedCount = selectedRowsForMassAction.length;

    const selectableVisibleRows = useMemo(
        () => (rows || []).filter((row) => isRowMassOperable(row)),
        [rows, isRowMassOperable],
    );

    const allVisibleSelected =
        selectableVisibleRows.length > 0 &&
        selectableVisibleRows.every((row) => selectedIds.has(row.id));

    const someVisibleSelected =
        selectableVisibleRows.some((row) => selectedIds.has(row.id)) && !allVisibleSelected;

    const resetMassState = useCallback(() => {
        setMassModalOpen(false);
        setMassOperationType(MASS_OPERATION_TYPE.CANCEL_AND_REFUND);
        setRefundMode(REFUND_MODE.FULL);
        setRefundValue('100');
        setMassResults([]);
        setMassExecutionProgress({ completed: 0, total: 0 });
        setIsExecutingMassAction(false);
        setExcludedFromMassActionById({});
        setMaxRefundableSummary({
            loading: false,
            error: '',
            totalAmount: 0,
            bookings: [],
        });
        refundableAmountCacheRef.current = new Map();
        selectAllCacheRef.current = { key: '', rowsById: {} };
    }, []);

    useEffect(() => {
        setSelectedRowsById({});
        resetMassState();
    }, [
        filters?.bookingId,
        filters?.confirmationCode,
        filters?.timeslotId,
        filters?.showId,
        filters?.search,
        filters?.statuses,
        resetMassState,
    ]);

    useEffect(() => {
        setExcludedFromMassActionById((previousState) => {
            const nextState = {};
            Object.keys(previousState).forEach((bookingId) => {
                if (selectedRowsById[bookingId]) {
                    nextState[bookingId] = true;
                }
            });
            return nextState;
        });
    }, [selectedRowsById]);

    useEffect(() => {
        let isCancelled = false;

        if (!massModalOpen || selectedRows.length === 0) {
            setMaxRefundableSummary({
                loading: false,
                error: '',
                totalAmount: 0,
                bookings: [],
            });
            return () => {
                isCancelled = true;
            };
        }

        if (massOperationType === MASS_OPERATION_TYPE.CANCEL_WITHOUT_REFUND) {
            setMaxRefundableSummary({
                loading: false,
                error: '',
                totalAmount: 0,
                bookings: [],
            });
            return () => {
                isCancelled = true;
            };
        }

        const loadMaxRefundable = async () => {
            setMaxRefundableSummary((prev) => ({
                ...prev,
                loading: true,
                error: '',
            }));

            try {
                const bookings = await Promise.all(
                    selectedRows.map(async (row) => {
                        const amount = await calculateRefundableAmount(
                            row.id,
                            refundableAmountCacheRef.current,
                        );
                        return {
                            id: row.id,
                            bookingName: getBookingDisplayName(row),
                            amount,
                        };
                    }),
                );

                if (isCancelled) {
                    return;
                }

                const totalAmount = bookings.reduce((sum, item) => sum + item.amount, 0);
                setMaxRefundableSummary({
                    loading: false,
                    error: '',
                    totalAmount,
                    bookings,
                });
            } catch (_error) {
                if (isCancelled) {
                    return;
                }
                setMaxRefundableSummary({
                    loading: false,
                    error: t('bookings.massAction.selectAllError'),
                    totalAmount: 0,
                    bookings: [],
                });
            }
        };

        loadMaxRefundable();

        return () => {
            isCancelled = true;
        };
    }, [massModalOpen, massOperationType, selectedRows, t]);

    const toggleRowSelection = useCallback((row, checked) => {
        setSelectedRowsById((prevState) => {
            const nextState = { ...prevState };
            if (checked) {
                nextState[row.id] = row;
            } else {
                delete nextState[row.id];
            }
            return nextState;
        });
    }, []);

    const clearSelection = useCallback(() => setSelectedRowsById({}), []);

    const setBookingIncludedInMassAction = useCallback((bookingId, included) => {
        setExcludedFromMassActionById((previousState) => {
            const key = String(bookingId);
            if (included) {
                if (!previousState[key]) {
                    return previousState;
                }
                const nextState = { ...previousState };
                delete nextState[key];
                return nextState;
            }

            if (previousState[key]) {
                return previousState;
            }
            return {
                ...previousState,
                [key]: true,
            };
        });
    }, []);

    const selectAllRowsAcrossPages = useCallback(async () => {
        if (!canUseMassAction || total <= 0) {
            return;
        }

        if (allVisibleSelected && selectedCount >= selectableVisibleRows.length) {
            setSelectedRowsById({});
            return;
        }

        const statusFilter =
            filters?.statuses && filters.statuses.length > 0
                ? filters.statuses.join(',')
                : undefined;

        const selectAllCacheKey = JSON.stringify({
            bookingId: filters?.bookingId,
            confirmationCode: filters?.confirmationCode,
            optionalId: filters?.timeslotId,
            showId: filters?.showId,
            search: filters?.search,
            statusFilter,
            total,
        });

        if (
            selectAllCacheRef.current.key === selectAllCacheKey &&
            Object.keys(selectAllCacheRef.current.rowsById || {}).length > 0
        ) {
            setSelectedRowsById(selectAllCacheRef.current.rowsById);
            return;
        }

        // Clear stale selection immediately to avoid transient UI flicker while loading all pages.
        setSelectedRowsById({});
        setSelectingAllPages(true);
        try {
            const collectedRowsById = {};
            let pageIndex = 0;
            let hasMorePages = true;

            while (hasMorePages) {
                const response = await getBookings({
                    bookingId: filters?.bookingId || undefined,
                    confirmationCode: filters?.confirmationCode || undefined,
                    optionalId: filters?.timeslotId || undefined,
                    showId: filters?.showId || undefined,
                    search: filters?.search || undefined,
                    statusFilter,
                    page: pageIndex,
                    pageSize: ALL_PAGES_PAGE_SIZE,
                });

                const responseRows = response?.bookings || [];

                responseRows.forEach((booking) => {
                    if (isRowMassOperable(booking)) {
                        collectedRowsById[booking.id] = booking;
                    }
                });

                hasMorePages = responseRows.length >= ALL_PAGES_PAGE_SIZE;
                pageIndex += 1;
            }

            const collectedCount = Object.keys(collectedRowsById).length;
            if (collectedCount === 0) {
                setSelectedRowsById({});
                selectAllCacheRef.current = { key: selectAllCacheKey, rowsById: {} };
                notifyError(t('bookings.massAction.noOperableBookings'));
                return;
            }

            selectAllCacheRef.current = { key: selectAllCacheKey, rowsById: collectedRowsById };
            setSelectedRowsById(collectedRowsById);
        } catch (error) {
            notifyError(getErrorMessage(error, t('bookings.massAction.selectAllError')));
        } finally {
            setSelectingAllPages(false);
        }
    }, [
        allVisibleSelected,
        canUseMassAction,
        filters,
        isRowMassOperable,
        notifyError,
        selectableVisibleRows.length,
        selectedCount,
        t,
        total,
    ]);

    const executeMassOperation = useCallback(async () => {
        if (selectedRowsForMassAction.length === 0) {
            notifyError(t('bookings.massAction.noSelection'));
            return;
        }
        if (isInvalidCancelRefundAmount) {
            notifyError(t('bookings.massAction.invalidRefundAmount'));
            return;
        }

        setIsExecutingMassAction(true);
        setMassResults([]);
        setMassExecutionProgress({ completed: 0, total: massSelectedCount });

        const results = [];

        for (let index = 0; index < selectedRowsForMassAction.length; index++) {
            const row = selectedRowsForMassAction[index];
            const bookingName = getBookingDisplayName(row);

            try {
                const amount =
                    massOperationType === MASS_OPERATION_TYPE.CANCEL_WITHOUT_REFUND
                        ? 0
                        : await calculateAmountForRow({
                              bookingId: row.id,
                              refundMode,
                              refundValue,
                              refundableAmountCache: refundableAmountCacheRef.current,
                          });

                if (
                    massOperationType === MASS_OPERATION_TYPE.CANCEL_AND_REFUND ||
                    massOperationType === MASS_OPERATION_TYPE.CANCEL_WITHOUT_REFUND
                ) {
                    await cancelBooking(row.id, {
                        amount,
                        reservationHash: row.reservationHash,
                    });
                } else {
                    await simpleRefundBooking(row.id, { amount });
                }

                results.push({
                    id: row.id,
                    bookingName,
                    status: 'success',
                    message: t('bookings.massAction.resultSuccess'),
                });
            } catch (error) {
                results.push({
                    id: row.id,
                    bookingName,
                    status: 'failed',
                    message: getErrorMessage(error, t('bookings.massAction.resultFailed')),
                });
            }

            setMassExecutionProgress({ completed: index + 1, total: massSelectedCount });
            setMassResults([...results]);
        }

        setIsExecutingMassAction(false);

        const failureCount = results.filter((result) => result.status === 'failed').length;
        if (failureCount === 0) {
            notifySuccess(t('bookings.massAction.completedSuccess', { count: results.length }));
        } else {
            notifyError(t('bookings.massAction.completedWithFailures', { failureCount }));
        }

        if (typeof refreshBookings === 'function') {
            await refreshBookings();
        }
    }, [
        massOperationType,
        notifyError,
        notifySuccess,
        refreshBookings,
        refundMode,
        refundValue,
        selectedRowsForMassAction,
        massSelectedCount,
        t,
        isInvalidCancelRefundAmount,
    ]);

    const executionPercent =
        massExecutionProgress.total > 0
            ? Math.round((massExecutionProgress.completed / massExecutionProgress.total) * 100)
            : 0;

    const hasCompletedExecution =
        !isExecutingMassAction &&
        massExecutionProgress.total > 0 &&
        massExecutionProgress.completed === massExecutionProgress.total;

    const massRefundPreview = useMemo(() => {
        if (maxRefundableSummary.loading || maxRefundableSummary.error) {
            return {
                totalAmount: 0,
                bookings: [],
            };
        }

        const fixedValueInCents = Math.max(0, Math.floor((Number(refundValue) || 0) * 100));
        const percentage = Math.min(100, Math.max(0, Number(refundValue) || 0));

        const bookings = maxRefundableSummary.bookings.map((booking) => {
            const maxAmount = Math.max(0, booking.amount || 0);
            const included = !excludedFromMassActionById[booking.id];
            let plannedAmount = 0;

            if (massOperationType === MASS_OPERATION_TYPE.CANCEL_WITHOUT_REFUND) {
                plannedAmount = 0;
            } else if (refundMode === REFUND_MODE.FULL) {
                plannedAmount = maxAmount;
            } else if (refundMode === REFUND_MODE.PERCENTAGE) {
                plannedAmount = Math.max(0, Math.floor((maxAmount * percentage) / 100));
            } else {
                plannedAmount = Math.min(fixedValueInCents, maxAmount);
            }

            return {
                ...booking,
                included,
                maxAmount,
                plannedAmount,
            };
        });

        return {
            totalAmount: bookings.reduce(
                (sum, booking) => sum + (booking.included ? booking.plannedAmount : 0),
                0,
            ),
            bookings,
        };
    }, [
        excludedFromMassActionById,
        maxRefundableSummary.bookings,
        maxRefundableSummary.error,
        maxRefundableSummary.loading,
        massOperationType,
        refundMode,
        refundValue,
    ]);

    return {
        selectedCount,
        massSelectedCount,
        selectedIds,
        allVisibleSelected,
        someVisibleSelected,
        selectingAllPages,
        massModalOpen,
        setMassModalOpen,
        massOperationType,
        setMassOperationType,
        refundMode,
        setRefundMode,
        refundValue,
        setRefundValue,
        isExecutingMassAction,
        massExecutionProgress,
        massResults,
        executionPercent,
        hasCompletedExecution,
        isInvalidCancelRefundAmount,
        maxRefundableSummary,
        massRefundPreview,
        setBookingIncludedInMassAction,
        toggleRowSelection,
        selectAllRowsAcrossPages,
        clearSelection,
        executeMassOperation,
        resetMassState,
    };
}

export default useBookingsMassAction;
